"use client";

import { useEffect, useState } from "react";
import EditableList from "@/app/(components)/generics/EditableList/EditableList";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import SiteFavicon from "@/app/(components)/generics/SiteFavicon/SiteFavicon";
import { Globals } from "@/app/vars/Globals";
import { Limits } from "@/app/vars/Limits";
import { getLinkDomain, openLinkFromRow, ProspectDisplayMode } from "./ProspectStrategy.logic";
import styles from "./ProspectStrategy.module.css";
import ProspectStrategyAddValue from "./ProspectStrategyAddValue";

type Props = {
  values: string[];
  display: ProspectDisplayMode;
  lang: string;
  color?: string;
  onSave?: (items: string[]) => void;
};

export default function ProspectStrategyValues(props: Props) {
  const { values, display, lang, color, onSave } = props;

  const [items, setItems] = useState(values);
  const [adding, setAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  useEffect(() => {
    setItems(values);
  }, [values]);

  const editable = !!onSave;
  const maxItems = Globals.MAX_PROSPECT_CRITERIA_ITEMS;

  const commit = (next: string[]) => {
    setItems(next);
    onSave?.(next);
  };

  const editAt = (index: number, value: string) => {
    const next = [...items];
    if (value.trim() === "") {
      next.splice(index, 1);
    } else {
      next[index] = value;
    }
    commit(next);
  };

  const deleteAt = (index: number) => {
    const next = [...items];
    next.splice(index, 1);
    commit(next);
  };

  const add = (value: string) => {
    setAdding(false);
    if (value.trim() !== "") {
      commit([...items, value]);
    }
  };

  if (display === ProspectDisplayMode.LIST && editable) {
    return (
      <EditableList
        items={items}
        onSave={commit}
        maxItems={maxItems}
        itemMaxLength={Limits.MAX_PROSPECT_STRATEGY_VALUE}
        lang={lang}
        bulletColor={color}
        listClassName={styles.editableList}
      />
    );
  }

  if (display === ProspectDisplayMode.LIST) {
    return (
      <ul className={styles.list}>
        {items.map((value, index) => (
          <li key={index} className={styles.listItem}>
            {value}
          </li>
        ))}
      </ul>
    );
  }

  if (display === ProspectDisplayMode.LINK) {
    return (
      <div className={styles.links}>
        {items.map((url, index) =>
          editable ? (
            <div
              key={index}
              className={styles.linkEdit}
              onClick={(e) => openLinkFromRow(e.target as HTMLElement, url)}
            >
              <SiteFavicon link={url} className={styles.favicon} />
              <EditableText
                value={url}
                maxLength={Limits.MAX_LINK}
                displayText={getLinkDomain(url)}
                onSave={(value) => editAt(index, value)}
                onEditingChange={(isEditing) => setEditingIndex(isEditing ? index : null)}
                alignCenter={true}
                className={styles.linkEditText}
                textClassName={styles.linkEditTextDisplay}
              />
              {editingIndex !== index && (
                <button type="button" className={styles.itemDelete} onClick={() => deleteAt(index)}>
                  <IconTrash />
                </button>
              )}
            </div>
          ) : (
            <a
              key={index}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              <SiteFavicon link={url} className={styles.favicon} />
              <span className={styles.linkText}>{getLinkDomain(url)}</span>
            </a>
          ),
        )}

        {editable && (
          <ProspectStrategyAddValue
            adding={adding}
            canAdd={items.length < maxItems}
            lang={lang}
            onStart={() => setAdding(true)}
            onAdd={add}
            onCancel={() => setAdding(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.badges}>
      {items.map((value, index) =>
        editable ? (
          <span key={index} className={styles.chipEdit}>
            <EditableText
              value={value}
              maxLength={Limits.MAX_PROSPECT_STRATEGY_VALUE}
              onSave={(val) => editAt(index, val)}
              onEditingChange={(isEditing) => setEditingIndex(isEditing ? index : null)}
              className={styles.chipEditText}
            />
            {editingIndex !== index && (
              <button type="button" className={styles.itemDelete} onClick={() => deleteAt(index)}>
                <IconTrash />
              </button>
            )}
          </span>
        ) : (
          <span key={index} className={styles.chip}>
            {value}
          </span>
        ),
      )}

      {editable && (
        <ProspectStrategyAddValue
          adding={adding}
          canAdd={items.length < maxItems}
          lang={lang}
          onStart={() => setAdding(true)}
          onAdd={add}
          onCancel={() => setAdding(false)}
        />
      )}
    </div>
  );
}
